import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import { Box, Collapse, Fab, IconButton, Tooltip } from '@mui/material';
import {
  isProductSelectionDebugAllowed,
  readProductDebugPanelOpen,
  writeProductDebugPanelOpen,
} from '../../helpers/productSelectionDebug';
import ProductSelectionDebugPanel from './ProductSelectionDebugPanel';

/**
 * Icône bas-gauche (style bandeau cookies) pour ouvrir/fermer le debug produit.
 * Visible uniquement en dev / recette.
 */
export default function ProductSelectionDebugWidget({ debug }) {
  const allowed = isProductSelectionDebugAllowed();
  const [open, setOpen] = useState(readProductDebugPanelOpen);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      writeProductDebugPanelOpen(next);
      return next;
    });
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    writeProductDebugPanelOpen(false);
  }, []);

  if (!allowed || typeof document === 'undefined') return null;

  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        left: 16,
        /* Au-dessus du panier mobile (z 20) et du bandeau cookies (bas-gauche) */
        bottom: { xs: 88, sm: 88, md: 20 },
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        gap: 1,
        pointerEvents: 'none',
      }}
    >
      <Collapse
        in={open}
        sx={{
          pointerEvents: 'auto',
          width: { xs: 'min(420px, calc(100dvw - 32px))', sm: 420 },
        }}
      >
        <Box sx={{ position: 'relative', mb: 0.5 }}>
          <IconButton
            size="small"
            onClick={close}
            aria-label="Fermer le debug produit"
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              zIndex: 2,
              color: '#ccc',
              bgcolor: 'rgba(0,0,0,0.35)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <ProductSelectionDebugPanel debug={debug} embedded />
        </Box>
      </Collapse>

      <Tooltip title={open ? 'Masquer le debug produit' : 'Debug sélection produit (dev/recette)'} placement="right">
        <Fab
          size="small"
          color={open ? 'primary' : 'default'}
          onClick={toggle}
          aria-label={open ? 'Masquer le debug produit' : 'Afficher le debug produit'}
          sx={{
            pointerEvents: 'auto',
            flexShrink: 0,
            boxShadow: 3,
            bgcolor: open ? undefined : 'rgba(30, 30, 36, 0.88)',
            color: open ? undefined : '#4ec9b0',
            '&:hover': { bgcolor: open ? undefined : 'rgba(40, 40, 48, 0.95)' },
          }}
        >
          <BugReportIcon fontSize="small" />
        </Fab>
      </Tooltip>
    </Box>,
    document.body,
  );
}
